import React, { useState, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { UploadStepsEnum, getDocTypeTitle, getDocTypeMessage, processPdfFile } from './renaiFuncs';
import { LoadingIcon } from '../../Components/Loading';
import styles from './RenAI.module.scss';

interface UploadPdfModalProps {
    show: boolean;
    onHide: () => void;
    onAnalyze: (file: File, images: string[]) => Promise<void>;
    title: string;
    docType: 'aps' | 'mortgage';
    appCode: string;
}

export const UploadPDFModal: React.FC<UploadPdfModalProps> = (props) => {
    const { show, onHide, onAnalyze, title } = props;
    const [step, setStep] = useState<UploadStepsEnum>(UploadStepsEnum.BeforeUpload);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);
    const docTypeTitle = getDocTypeTitle(props.docType);
    const docTypeMessage = getDocTypeMessage(props.docType, props.appCode);
    const formControlRef = useRef<HTMLInputElement>(null);

    const resetUploadState = () => {
        setStep(UploadStepsEnum.BeforeUpload);
        setErrorMsg(null);
        setIsLoading(false);
        setIsAnalyzing(false);
        setIsDragging(false);
        setCurrentPage(0);
        setTotalPages(0);
        if (formControlRef.current) {
            formControlRef.current.value = '';
        }
    };

    const handleCancel = () => {
        resetUploadState();
        onHide();
    };

    const handleFileSelection = async (file: File) => {
        if (!file) return;

        setStep(UploadStepsEnum.Uploading);
        setIsLoading(true);
        setCurrentPage(0);
        setTotalPages(0);

        await processPdfFile(file, file.name, {
            onSuccess: async (images) => {
                setIsLoading(false);
                setIsAnalyzing(true);
                
                try {
                    await onAnalyze(file, images);
                    setIsAnalyzing(false);
                    resetUploadState();
                } catch (error) {
                    setIsAnalyzing(false);
                    setErrorMsg("Failed to analyze the document.");
                    setStep(UploadStepsEnum.ProcessingFail);
                }
            },
            onFailure: (errorCode) => {
                resetUploadState();
                setErrorMsg(errorCode || "Failed to process the PDF file.");
                setStep(errorCode ? UploadStepsEnum.ProcessingFail : UploadStepsEnum.UploadFail);
            },
            onProgress: (currentPageNum: number, totalPagesNum: number) => {
                setCurrentPage(currentPageNum);
                setTotalPages(totalPagesNum);
            }
        });
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && !isLoading && !isAnalyzing) {
            handleFileSelection(file);
        }
        if (event.target) {
            event.target.value = '';
        }
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelection(file);
        }
    };

    const triggerFileInput = () => {
        if (formControlRef.current) {
            formControlRef.current.click();
        }
    };

    const DocumentUploadFailed = () => (
        <>
            <i 
                className={`fa-duotone fa-cloud-exclamation ${styles.statusFail} ${styles.dragDropIcon}`}
            ></i>
            <p className={styles.dragDropText}>
                <b>{errorMsg || "There was an error processing your document."}</b>
                <br />
                Please try again with a different document.
            </p>
        </>
    );

    const DragDropDocument = () => (
        <>
            <i 
                className={`fa-duotone fa-cloud-arrow-up ${styles.dragDropIcon}`}
            ></i>
            <div className={styles.dragDropText}>
                {/* this is not grammatically correct but it should cover all our cases */}
                Drag and drop {`a${docTypeMessage.match('^[aieouAIEOU].*')?"n":""} `}
                {docTypeMessage} for <br />
                <span className={styles.dragDropBrand}>
                <i className={`renai-icon renai-icon-light ${styles.renaiIcon}`}></i>
                    Document Analyzer</span> to analyze.
            </div>
            <div className={styles.dragDropDivider}>
                <div className={styles.dragDropDividerLine}></div>
                <span className={styles.dragDropDividerText}>or</span>
                <div className={styles.dragDropDividerLine}></div>
            </div>
            <Button
                variant="primary"
                className={styles.dragDropButton}
                onClick={(e) => {
                    e.stopPropagation();
                    triggerFileInput();
                }}
            >
                Select from Computer
            </Button>
        </>
    );

    const getLoadingModal = (message: string) => (
        <Modal 
            show={show} 
            onHide={handleCancel} 
            fullscreen="md-down" 
            centered
            backdrop="static"
        >
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-3">
                <LoadingIcon showMsg={true} extraMsg={message} inline={true} />
            </Modal.Body>
        </Modal>
    );

    if (isAnalyzing) {
        return getLoadingModal("Preparing results");
    }

    if (isLoading) {
        const loadingMessage = totalPages > 0 
            ? `Analyzing document (Page ${currentPage} of ${totalPages})`
            : "Analyzing document";
        return getLoadingModal(loadingMessage);
    }

    return (
        <Modal 
            show={show} 
            onHide={handleCancel} 
            fullscreen="md-down" 
            centered
            backdrop="static"
        >
            <Modal.Header>
                <Modal.Title>
                    <i className="renai-icon renai-pill"></i>
                    Import
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group controlId="docUploader">
                    <Form.Control 
                        type="file"
                        ref={formControlRef}
                        accept="application/pdf"
                        onChange={handleFileUpload}
                        className={styles.hiddenInput}
                    />

                    <div 
                        className={styles.fileUpload}
                        onClick={triggerFileInput}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {
                            (
                                errorMsg || [UploadStepsEnum.UploadFail, UploadStepsEnum.ProcessingFail].includes(step)
                            ) ? (
                                    <DocumentUploadFailed />
                            ) : (
                                    <DragDropDocument />
                            )}
                    </div>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer >
                <Button variant="text" onClick={handleCancel}>
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};